import { addDays, dateRangeInclusive, parseDateKey } from "../dateNav";
import { todayKey } from "../format";
import { reconcileTopAppsToTotal } from "../native/usageTotalReconciliation";
import { MARKETING_DEMO_DAY_COUNT } from "./constants";
import type {
  MarketingDemoDataset,
  MarketingDemoDaySnapshot,
  MarketingDemoHourlyBucket,
  MarketingDemoTimeBand,
  MarketingSeedApp,
} from "./types";

const TIME_BAND_KEYS = ["morning", "afternoon", "evening", "night"] as const;

function createSeededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function dayOfWeek(date: string): number {
  return parseDateKey(date).getDay();
}

function isWeekend(date: string): boolean {
  const dow = dayOfWeek(date);
  return dow === 0 || dow === 6;
}

function hourWeight(hour: number): number {
  if (hour >= 7 && hour <= 9) return 1.3;
  if (hour >= 12 && hour <= 13) return 0.9;
  if (hour >= 18 && hour <= 22) return 2.1;
  if (hour >= 23 || hour <= 5) return 0.35;
  return 0.75;
}

function distributeHourly(totalSeconds: number, opens: number, rng: () => number): MarketingDemoHourlyBucket[] {
  const weights = Array.from({ length: 24 }, (_, hour) => hourWeight(hour) * (0.85 + rng() * 0.3));
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  let assignedSeconds = 0;
  const buckets: MarketingDemoHourlyBucket[] = weights.map((weight, hour) => {
    const seconds = Math.round((totalSeconds * weight) / weightSum);
    assignedSeconds += seconds;
    return { hour, seconds, opens: 0 };
  });
  const drift = totalSeconds - assignedSeconds;
  if (drift !== 0) {
    const peakIndex = buckets.reduce(
      (best, bucket, index, list) => (bucket.seconds > list[best]!.seconds ? index : best),
      0,
    );
    buckets[peakIndex]!.seconds += drift;
  }

  let assignedOpens = 0;
  for (const bucket of buckets) {
    const share = totalSeconds > 0 ? bucket.seconds / totalSeconds : 0;
    bucket.opens = Math.max(0, Math.round(opens * share));
    assignedOpens += bucket.opens;
  }
  const openDrift = opens - assignedOpens;
  if (openDrift !== 0) {
    const peakIndex = buckets.reduce(
      (best, bucket, index, list) => ((bucket.opens ?? 0) > (list[best]!.opens ?? 0) ? index : best),
      0,
    );
    buckets[peakIndex]!.opens = (buckets[peakIndex]!.opens ?? 0) + openDrift;
  }

  return buckets.filter((bucket) => bucket.seconds > 0 || (bucket.opens ?? 0) > 0);
}

function hourlyToTimeBands(hourly: MarketingDemoHourlyBucket[]): MarketingDemoTimeBand[] {
  const bandForHour = (hour: number): MarketingDemoTimeBand["key"] => {
    if (hour >= 6 && hour < 12) return "morning";
    if (hour >= 12 && hour < 17) return "afternoon";
    if (hour >= 17 && hour < 22) return "evening";
    return "night";
  };
  const totals = new Map<MarketingDemoTimeBand["key"], { seconds: number; opens: number }>();
  for (const key of TIME_BAND_KEYS) totals.set(key, { seconds: 0, opens: 0 });
  for (const bucket of hourly) {
    const key = bandForHour(bucket.hour);
    const entry = totals.get(key)!;
    entry.seconds += bucket.seconds;
    entry.opens += bucket.opens ?? 0;
  }
  return TIME_BAND_KEYS.map((key) => {
    const entry = totals.get(key)!;
    return { key, seconds: entry.seconds, opens: entry.opens > 0 ? entry.opens : undefined };
  });
}

function appWeightMultiplier(app: MarketingSeedApp, dayIndex: number, date: string): number {
  let multiplier = 1;
  const chromeHeavy = dayIndex >= MARKETING_DEMO_DAY_COUNT - 21;
  if (chromeHeavy && app.displayName === "Chrome") multiplier *= 1.55;
  if (chromeHeavy && app.category === "Social Networking") multiplier *= 1.25;
  if (isWeekend(date) && (app.category === "Entertainment" || app.category === "Photo & Video")) {
    multiplier *= 1.2;
  }
  if (dayIndex % 31 < 2) multiplier *= 1.15;
  return multiplier;
}

function generateDaySnapshot(
  date: string,
  dayIndex: number,
  seedApps: MarketingSeedApp[],
  rng: () => number,
): MarketingDemoDaySnapshot {
  const weekend = isWeekend(date);
  const trend = 1 + (dayIndex / MARKETING_DEMO_DAY_COUNT) * 0.12;
  const spike = dayIndex % 28 < 3 ? 1.35 : 1;
  const baseTotal = weekend ? 19_800 + rng() * 8_400 : 13_200 + rng() * 6_600;
  const totalScreenTimeSeconds = Math.round(baseTotal * trend * spike);

  const weightedApps = seedApps.map((app) => ({
    ...app,
    effectiveWeight: app.weight * appWeightMultiplier(app, dayIndex, date) * (0.9 + rng() * 0.2),
  }));
  const weightSum = weightedApps.reduce((sum, app) => sum + app.effectiveWeight, 0);

  const rawApps = weightedApps.map((app) => {
    const durationSeconds = Math.round((totalScreenTimeSeconds * app.effectiveWeight) / weightSum);
    const opens = Math.max(1, Math.round(durationSeconds / 420 + rng() * 6));
    const hourly = distributeHourly(durationSeconds, opens, rng);
    return {
      appIdentifier: app.appIdentifier,
      displayName: app.displayName,
      category: app.category,
      durationSeconds,
      opens,
      hourly,
    };
  });

  const topApps = reconcileTopAppsToTotal(rawApps, totalScreenTimeSeconds).map((app) => ({
    appIdentifier: app.appIdentifier,
    displayName: app.displayName,
    category: app.category,
    durationSeconds: app.durationSeconds,
    opens: app.opens ?? 0,
    hourly: "hourly" in app ? app.hourly : undefined,
  }));

  const allHourly = topApps.flatMap((app) => app.hourly ?? []);
  const timeBands = hourlyToTimeBands(allHourly);
  const estimatedUnlockCount = Math.max(
    25,
    Math.round(totalScreenTimeSeconds / 180 + (weekend ? 18 : 8) + rng() * 20),
  );

  return {
    date,
    totalScreenTimeSeconds,
    estimatedUnlockCount,
    topApps,
    timeBands,
  };
}

export function generateMarketingDemoDataset(
  seedApps: MarketingSeedApp[],
  endDate: string = todayKey(),
): MarketingDemoDataset {
  const startDate = addDays(endDate, -(MARKETING_DEMO_DAY_COUNT - 1));
  const dates = dateRangeInclusive(startDate, endDate);
  const rng = createSeededRandom(0x5c0ee040);
  const days = dates.map((date, dayIndex) => generateDaySnapshot(date, dayIndex, seedApps, rng));
  const byDate = new Map(days.map((day) => [day.date, day]));
  return { startDate, endDate, days, byDate };
}

export function marketingDemoDatasetDayCount(dataset: MarketingDemoDataset): number {
  return dataset.days.length;
}

export function marketingDemoDatasetHasAppKeys(dataset: MarketingDemoDataset): boolean {
  return dataset.days.every((day) => day.topApps.length > 0);
}
